import Layout from "@components/layout/layout";
import BudgetStyleSelection from "@components/budget-2026-summariser/elements/budget-style-selection";
import BudgetTopicSelection from "@components/budget-2026-summariser/elements/budget-topic-selection";
import BudgetBreadcrumbs from "@components/budget-2026-summariser/ui/budget-breadcrumbs";
import BudgetChat from "@components/budget-2026-summariser/elements/budget-chat";
import { useAtom } from "jotai";
import {
  tabsApiAtom,
  tabsIndexAtom,
  tabDirectionAtom,
  containsStyle,
  selectedStyleAtom,
  interestedTopicsAtom,
  existingTopics,
} from "@components/budget-2026-summariser/constants/budget-atom";
import { useEffect, useRef, useCallback } from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import {
  tabValues,
  kBudgetSummariserSEO,
} from "@components/budget-2026-summariser/constants/budget-constants";
import { NextSeo } from "next-seo";

const Tabs = dynamic(() => import("@/components/ui/tabs").then((m) => m.Tabs), {
  ssr: false,
});
const TabsContent = dynamic(
  () => import("@/components/ui/tabs").then((m) => m.TabsContent),
  { ssr: false }
);

/// An implementation of the Tabs component API, since useTabControls will cause SSR errors
/// Must be implemented before the animated tabs component can be used
const BudgetTabsApiProvider = () => {
  const [tabIndex, setTabIndex] = useAtom(tabsIndexAtom);
  const [, setTabsApi] = useAtom(tabsApiAtom);
  const [, setTabDirection] = useAtom(tabDirectionAtom);

  // Track previous index for direction calculation
  const previousIndexRef = useRef(tabIndex);
  const currentTabValue = tabValues[tabIndex] || "style";

  // Updates tab index with automatic direction calculation
  const updateTabIndex = useCallback(
    (newIndex: number) => {
      if (
        newIndex >= 0 &&
        newIndex < tabValues.length &&
        newIndex !== tabIndex
      ) {
        const previousIndex = previousIndexRef.current;
        const direction =
          newIndex > previousIndex && newIndex
            ? /// 0 for the last item, due to overscrolling issue for last item
              newIndex == tabValues.length - 1
              ? 0
              : 1
            : -1;

        setTabDirection(direction);
        setTabIndex(newIndex);

        previousIndexRef.current = newIndex;
      }
    },
    [tabIndex, setTabIndex, setTabDirection]
  );

  useEffect(() => {
    const api = {
      scrollNext: () => {
        const nextIndex = Math.min(tabIndex + 1, tabValues.length - 1);
        updateTabIndex(nextIndex);
      },
      scrollPrev: () => {
        const prevIndex = Math.max(tabIndex - 1, 0);
        updateTabIndex(prevIndex);
      },
      scrollTo: (index: number) => {
        updateTabIndex(index);
      },
      scrollToValue: (value: string) => {
        const index = tabValues.indexOf(value);
        if (index !== -1) {
          updateTabIndex(index);
        }
      },
      currentIndex: tabIndex,
      totalTabs: tabValues.length,
      canScrollNext: tabIndex < tabValues.length - 1,
      canScrollPrev: tabIndex > 0,
      activeValue: currentTabValue,
    };

    setTabsApi(api);
  }, [tabIndex, updateTabIndex, currentTabValue, setTabsApi]);

  return null;
};

const BudgetLandingPage: React.FC = () => {
  const router = useRouter();
  const locale = router.locale || router.defaultLocale;
  const seoLocale = "/" + locale;
  const { query } = router;
  const [tabIndex, setTabIndex] = useAtom(tabsIndexAtom);
  const [tabDirection, setTabDirection] = useAtom(tabDirectionAtom);
  const currentTabValue = tabValues[tabIndex] || "style";
  const [, setSelectedStyle] = useAtom(selectedStyleAtom);
  const [, setInterestedTopics] = useAtom(interestedTopicsAtom);

  /// IMPORTANT: Ensures that the initialising of the page using query params is only
  /// done once, else it will repeatedly do so whenever a value is changed
  const hasInitialised = useRef(false);

  /// Handles query params and initialises values accordingly
  useEffect(() => {
    if (!router.isReady || !query.style || hasInitialised.current) return;

    const style = Array.isArray(query.style)
      ? query.style.at(0)
      : (query.style as string);

    if (style && containsStyle(style)) {
      setSelectedStyle(style);
      setTabIndex(1);

      const topics = Array.isArray(query.topic) ? query.topic : [query.topic];
      const validTopics = existingTopics(topics, 5);
      if (validTopics.length) {
        setInterestedTopics(validTopics);
        setTabIndex(2);
      }

      hasInitialised.current = true;
    }
  }, [
    router.isReady,
    query.style,
    query.topic,
    setInterestedTopics,
    setSelectedStyle,
    setTabIndex,
  ]);

  const handleValueChange = (newValue: string) => {
    const newIndex = tabValues.indexOf(newValue);
    if (newIndex !== -1 && newIndex !== tabIndex) {
      const direction = newIndex > tabIndex ? 1 : -1;

      setTabDirection(direction);
      setTabIndex(newIndex);
    }
  };

  /// Handle forward and backward navigation of browser
  useEffect(() => {
    const handlePopState = (event) => {
      if (event.state && event.state.url) {
        const newUrl = new URL(event.state.url, window.location.origin);
        const oldParams = Object.keys(router.query);
        const newParams = newUrl.searchParams;
        const newParamsLength = Array.from(newParams.entries()).length;
        const style = newParams.get("style");
        const topic = newParams.get("topic");
        const tabDirection = newParamsLength > oldParams.length ? 1 : -1;

        setTabDirection(tabDirection);

        if (topic != null && style != null) {
          setTabIndex(2);
        } else if (topic == null && style != null) {
          setTabIndex(1);
          return false;
        } else {
          setTabIndex(0);
          return false;
        }
      }
      return true;
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [router, setTabDirection, setTabIndex]);

  return (
    <>
      <NextSeo
        title={kBudgetSummariserSEO["title"]}
        description={kBudgetSummariserSEO["description"]}
        noindex={false}
        nofollow={false}
        openGraph={{
          url: `https://luminews.my${
            seoLocale.includes("en") ? "" : seoLocale
          }${router.asPath}`,
          title: kBudgetSummariserSEO["title"],
          description: kBudgetSummariserSEO["description"],
          images: [
            {
              url: "/manglish_budget_summariser_metaimage.webp",
              alt: "Lumi News Malaysia",
            },
          ],
        }}
      />

      <Layout
        showNewsStacks={false}
        showCTA={false}
        showFooter={false}
        className="!overflow-visible"
        retainWholePage={true}
        hideLocaleOptions={true}
      >
        <div className="mx-auto max-w-xl">
          <BudgetBreadcrumbs index={tabIndex} />
          <BudgetTabsApiProvider />

          <Tabs
            defaultValue="style"
            value={currentTabValue}
            direction={tabDirection}
            onValueChange={handleValueChange}
          >
            <TabsContent value="style" animated={true}>
              <BudgetStyleSelection />
            </TabsContent>

            <TabsContent value="topics" animated={true}>
              <BudgetTopicSelection />
            </TabsContent>

            <TabsContent value="chat" animated={true}>
              <BudgetChat />
            </TabsContent>
          </Tabs>
        </div>
      </Layout>
    </>
  );
};

export default BudgetLandingPage;
